import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientLogo } from '@/components/ClientLogo';
import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Timestamp } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { AVAILABILITY_STATUS_METADATA, AvailabilityStatuses, type AvailabilityStatus } from '../../models';
import { useAvailabilityListQuery } from '../../data';
import { availabilityQueryKeys } from '../../data/availabilityQueryKeys';
import { deleteAvailabilityById, updateAvailability } from '../../services/availabilityService';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_BADGE_CLASSES: Record<AvailabilityStatus, string> = {
  Simulation: 'border-blue-200 bg-blue-50 text-blue-600',
  Prospection: 'border-emerald-200 bg-emerald-50 text-emerald-600',
  Obligation: 'border-rose-300 bg-rose-50 text-rose-600',
  Completed: 'border-purple-200 bg-purple-50 text-purple-600',
  Cancelled: 'border-gray-200 bg-gray-100 text-gray-600',
};

const STATUS_FILTER_ORDER: AvailabilityStatus[] = [
  AvailabilityStatuses.Obligation,
  AvailabilityStatuses.Prospection,
  AvailabilityStatuses.Simulation,
  AvailabilityStatuses.Completed,
  AvailabilityStatuses.Cancelled,
];

export default function CollaboratorAvailabilitiesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError, warning } = useToast();
  const { user } = useAuth();
  const [processingState, setProcessingState] = React.useState<{
    id: string;
    action: 'cancel' | 'delete';
  } | null>(null);
  const {
    data: availabilities,
    isLoading,
    isError,
    error: queryError,
  } = useAvailabilityListQuery();
  const [selectedStatuses, setSelectedStatuses] = React.useState<AvailabilityStatus[]>([
    AvailabilityStatuses.Obligation,
  ]);

  const statusCounts = useMemo(() => {
    const counts: Record<AvailabilityStatus, number> = {
      [AvailabilityStatuses.Simulation]: 0,
      [AvailabilityStatuses.Prospection]: 0,
      [AvailabilityStatuses.Obligation]: 0,
      [AvailabilityStatuses.Completed]: 0,
      [AvailabilityStatuses.Cancelled]: 0,
    };

    availabilities.forEach((availability) => {
      const status = availability.status as AvailabilityStatus;
      counts[status] = (counts[status] ?? 0) + 1;
    });

    return counts;
  }, [availabilities]);

  const overviewSummary = useMemo(() => {
    const todayStart = startOfDay(new Date());
    let pendingHires = 0;
    let pendingAllocations = 0;
    let nextFuture: Date | null = null;
    let nextAny: Date | null = null;

    availabilities.forEach((availability) => {
      const status = availability.status as AvailabilityStatus;
      const isInactive =
        status === AvailabilityStatuses.Cancelled || status === AvailabilityStatuses.Completed;

      const slots = availability.slots ?? [];

      if (!isInactive) {
        slots.forEach((slot) => {
          const strategy = slot.fulfillmentStrategy ?? 'known';
          if (strategy === 'hire') {
            pendingHires += 1;
          } else if (!slot.chosenCollaboratorId) {
            pendingAllocations += 1;
          }
        });
      }

      const referenceDate = availability.referenceDate?.toDate?.();
      if (referenceDate) {
        if (!nextAny || referenceDate.getTime() < nextAny.getTime()) {
          nextAny = referenceDate;
        }

        if (!isInactive && referenceDate.getTime() >= todayStart.getTime()) {
          if (!nextFuture || referenceDate.getTime() < nextFuture.getTime()) {
            nextFuture = referenceDate;
          }
        }
      }
    });

    const resolvedDeadline = nextFuture ?? nextAny;

    return {
      pendingHires,
      pendingAllocations,
      nextDeadline: resolvedDeadline,
      nextDeadlineLabel: resolvedDeadline
        ? format(resolvedDeadline, 'eee, dd/MM', { locale: ptBR })
        : 'Sem previsão',
    };
  }, [availabilities]);

  const filteredAvailabilities = useMemo(
    () =>
      availabilities.filter((availability) =>
        selectedStatuses.includes(availability.status as AvailabilityStatus)
      ),
    [availabilities, selectedStatuses]
  );

  const handleToggleStatus = useCallback((status: AvailabilityStatus) => {
    setSelectedStatuses((current) => {
      if (current.includes(status)) {
        if (current.length === 1) {
          return current;
        }
        return current.filter((item) => item !== status);
      }

      return [...current, status];
    });
  }, []);
  const handleOpenAvailability = useCallback(
    (availabilityId: string) => {
      if (!availabilityId) {
        return;
      }

      const searchParams = new URLSearchParams({ availabilityId });
      navigate(`/collaborators/availabilities/form?${searchParams.toString()}`);
    },
    [navigate]
  );

  const invalidateAvailabilityQueries = useCallback(
    async (availabilityId: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: availabilityQueryKeys.list() }),
        queryClient.invalidateQueries({ queryKey: availabilityQueryKeys.detail(availabilityId) }),
      ]);
    },
    [queryClient]
  );

  const handleCancelAvailability = useCallback(
    async (availabilityId: string, currentStatus: AvailabilityStatus) => {
      if (!availabilityId) {
        warning('Operação indisponível', 'Não foi possível identificar a necessidade.');
        return;
      }

      if (currentStatus === AvailabilityStatuses.Cancelled) {
        warning('Necessidade já cancelada', 'Esta disponibilidade já está com status cancelado.');
        return;
      }

      const confirmed = window.confirm(
        'Deseja cancelar esta necessidade? O status será atualizado para Cancelada.'
      );
      if (!confirmed) {
        return;
      }

      setProcessingState({ id: availabilityId, action: 'cancel' });
      try {
        await updateAvailability({
          id: availabilityId,
          status: AvailabilityStatuses.Cancelled,
          updatedAt: Timestamp.now(),
          updatedById: user?.uid ?? null,
          updatedByName: user?.displayName ?? user?.email ?? null,
          updatedByAvatar: user?.photoURL ?? null,
        });

        await invalidateAvailabilityQueries(availabilityId);
        success('Disponibilidade cancelada', 'A necessidade foi marcada como cancelada.');
      } catch (err) {
        console.error('[availability-list] erro ao cancelar disponibilidade', err);
        const message = err instanceof Error ? err.message : 'Erro ao cancelar a disponibilidade.';
        showError('Não foi possível cancelar a disponibilidade', message);
      } finally {
        setProcessingState(null);
      }
    },
    [invalidateAvailabilityQueries, showError, success, user, warning]
  );

  const handleDeleteAvailability = useCallback(
    async (availabilityId: string) => {
      if (!availabilityId) {
        warning('Operação indisponível', 'Não foi possível identificar a necessidade.');
        return;
      }

      const confirmed = window.confirm(
        'Deseja remover esta necessidade? Esta ação é irreversível.'
      );
      if (!confirmed) {
        return;
      }

      setProcessingState({ id: availabilityId, action: 'delete' });
      try {
        await deleteAvailabilityById(availabilityId);
        await invalidateAvailabilityQueries(availabilityId);
        success('Disponibilidade removida', 'A necessidade foi excluída com sucesso.');
      } catch (err) {
        console.error('[availability-list] erro ao remover disponibilidade', err);
        const message = err instanceof Error ? err.message : 'Erro ao excluir a disponibilidade.';
        showError('Não foi possível excluir a disponibilidade', message);
      } finally {
        setProcessingState(null);
      }
    },
    [invalidateAvailabilityQueries, showError, success, warning]
  );

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="flex items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50 p-6 text-indigo-600">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-indigo-500" />
        </span>
        <span className="text-sm font-medium">Carregando disponibilidades cadastradas...</span>
      </div>
    );
  } else if (isError) {
    content = (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Não foi possível carregar as disponibilidades registradas.
        {queryError?.message ? (
          <span className="ml-1 font-medium">{queryError.message}</span>
        ) : null}
      </div>
    );
  } else if (availabilities.length === 0) {
    content = (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50 text-indigo-500">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-8 w-8"
          >
            <path d="M12 2a7 7 0 0 1 7 7v1.382a3 3 0 0 0 .879 2.121l.293.293a1 1 0 0 1-.707 1.707H17v1a5 5 0 0 1-10 0v-1H3.535a1 1 0 0 1-.707-1.707l.293-.293A3 3 0 0 0 4 10.382V9a7 7 0 0 1 7-7Zm0 18a3 3 0 0 0 2.995-2.824L15 17h-6a3 3 0 0 0 2.824 2.995L12 20Zm0-16a5 5 0 0 0-4.995 4.783L7 9v1.382a5 5 0 0 1-1.463 3.536l-.051.05h12.927l-.051-.05A5 5 0 0 1 17 10.382V9a5 5 0 0 0-5-5Z" />
          </svg>
        </div>

        <div className="mt-6 space-y-2">
          <h2 className="text-xl font-semibold text-gray-900">Nenhuma necessidade cadastrada ainda</h2>
          <p className="mx-auto max-w-xl text-sm text-gray-500">
            Registre uma nova disponibilidade para começar a acompanhar demandas e oportunidades futuras.
          </p>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contratações pendentes
            </span>
            <p className="mt-2 text-2xl font-semibold text-gray-900">
              {overviewSummary.pendingHires}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Alocações pendentes
            </span>
            <p className="mt-2 text-2xl font-semibold text-gray-900">
              {overviewSummary.pendingAllocations}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Próximo deadline
            </span>
            <p
              className={`mt-2 text-xl font-semibold ${
                overviewSummary.nextDeadline ? 'text-gray-900' : 'text-gray-500'
              }`}
            >
              {overviewSummary.nextDeadlineLabel}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:col-span-2 lg:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filtrar por status
            </span>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {STATUS_FILTER_ORDER.map((status) => {
                const label = AVAILABILITY_STATUS_METADATA[status]?.label ?? status;
                const count = statusCounts[status] ?? 0;
                const checked = selectedStatuses.includes(status);
                const badgeClasses =
                  STATUS_BADGE_CLASSES[status] ?? 'border-gray-200 bg-gray-50 text-gray-600';
                return (
                  <label
                    key={status}
                    className={`flex items-center gap-3 rounded-full border px-3 py-2 text-xs font-semibold shadow-sm transition focus-within:ring-2 focus-within:ring-current focus-within:ring-offset-1 ${badgeClasses}`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-current focus:ring-current"
                        checked={checked}
                        onChange={() => handleToggleStatus(status)}
                      />
                      <span>{label}</span>
                    </span>
                    <span className="text-[11px] text-current">{count}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {filteredAvailabilities.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-600">
            Nenhuma necessidade corresponde aos filtros selecionados.
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {filteredAvailabilities.map((availability) => {
              const status = availability.status as AvailabilityStatus;
              const statusMeta = AVAILABILITY_STATUS_METADATA[status];
              const badgeClasses =
                STATUS_BADGE_CLASSES[status] ?? 'border-gray-200 bg-gray-50 text-gray-600';
              const isProcessingCurrent = processingState?.id === availability.id;
              const isCanceling = isProcessingCurrent && processingState?.action === 'cancel';
              const isDeleting = isProcessingCurrent && processingState?.action === 'delete';
              const isCancelled = status === AvailabilityStatuses.Cancelled;
              const referenceLabel = (() => {
                const referenceDate = availability.referenceDate?.toDate?.();
                if (!referenceDate) {
                  return 'Data de referência não informada.';
                }

                const today = startOfDay(new Date());
                const diff = differenceInCalendarDays(referenceDate, today);

                if (diff > 0) {
                  const weekday = format(referenceDate, 'eee', { locale: ptBR });
                  const dayMonth = format(referenceDate, 'dd/MM', { locale: ptBR });
                  const plural = diff === 1 ? 'dia' : 'dias';
                  return `Daqui há ${diff} ${plural}, ${weekday}, ${dayMonth}.`;
                }

                if (diff === 0) {
                  return 'Hoje.';
                }

                const overdue = Math.abs(diff);
                const plural = overdue === 1 ? 'dia' : 'dias';
                return `${overdue} ${plural} atrasado.`;
              })();
              return (
                <div
                  key={availability.id}
                  role="button"
                tabIndex={0}
                aria-label={`Editar disponibilidade ${availability.initiative}`}
                onClick={() => handleOpenAvailability(availability.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenAvailability(availability.id);
                  }
                }}
                className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition duration-150 hover:-translate-y-1 hover:shadow-md cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                title={availability.clientName ? `Cliente: ${availability.clientName}` : undefined}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-5">
                    <ClientLogo
                      client={{
                        id: availability.clientId ?? null,
                        name: availability.clientName ?? null,
                        logoUrl: availability.clientLogo ?? null,
                      }}
                      clientId={availability.clientId ?? undefined}
                      variant={64}
                      className="h-16 w-16 shrink-0"
                    />
                    <div className="flex min-w-0 flex-col gap-1">
                      <h3 className="text-left text-base font-semibold text-gray-900">
                        {availability.initiative}
                      </h3>
                      <span className="text-xs text-gray-500">{referenceLabel}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${badgeClasses}`}
                    >
                      {statusMeta?.label ?? status}
                    </span>
                    <div className="flex items-center gap-1 pt-1">
                      {!isCancelled ? (
                        <button
                          type="button"
                          aria-label="Cancelar necessidade"
                          title="Cancelar necessidade"
                          className="rounded-full bg-amber-50 p-1.5 text-amber-600 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={isCanceling || isDeleting}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void handleCancelAvailability(availability.id, status);
                          }}
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label="Excluir necessidade"
                        title="Excluir necessidade"
                        className="rounded-full bg-red-50 p-1.5 text-red-600 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isCanceling || isDeleting}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          void handleDeleteAvailability(availability.id);
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <hr className="border-t border-gray-100" />

                <div className="space-y-2">
                  {availability.slots.map((slot) => {
                    const primaryCapability = slot.capabilities?.find((cap) => cap?.isPrimary) ??
                      (slot.capabilities && slot.capabilities.length > 0 ? slot.capabilities[0] : null);
                    const primaryCapabilityLabel = primaryCapability
                      ? [primaryCapability.typeName, primaryCapability.technologyName, primaryCapability.seniorityName]
                          .filter(Boolean)
                          .join(' • ')
                      : null;
                    const additionalCapabilitiesCount = primaryCapability
                      ? Math.max(slot.capabilities.length - 1, 0)
                      : slot.capabilities.length;

                    return (
                      <div key={slot.slotId} className="flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-gray-900">
                              {slot.title && slot.title.trim().length > 0 ? slot.title : 'Vaga sem título'}
                            </span>
                            {primaryCapabilityLabel ? (
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span>{primaryCapabilityLabel}</span>
                                {additionalCapabilitiesCount > 0 ? (
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                    +{additionalCapabilitiesCount}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Nenhuma habilidade definida</span>
                            )}
                          </div>
                        </div>

                        {(() => {
                          const chosenCandidate = slot.candidates?.find(
                            (candidate) => candidate?.collaboratorId === slot.chosenCollaboratorId
                          );

                          if (chosenCandidate) {
                            const initials = (chosenCandidate.collaboratorName ?? '??')
                              .slice(0, 2)
                              .toUpperCase();

                            return (
                              <div className="flex flex-col gap-2 rounded-md border border-indigo-100 bg-indigo-50 p-3">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                                  Colaborador escolhido
                                </span>
                                <div className="flex items-center gap-3">
                                  {chosenCandidate.collaboratorAvatar ? (
                                    <img
                                      src={chosenCandidate.collaboratorAvatar}
                                      alt={chosenCandidate.collaboratorName ?? 'Colaborador escolhido'}
                                      className="h-9 w-9 rounded-full object-cover shadow"
                                    />
                                  ) : (
                                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                                      {initials}
                                    </span>
                                  )}
                                  <div className="flex flex-col">
                                    <span className="text-sm font-semibold text-gray-900">
                                      {chosenCandidate.collaboratorName ?? 'Colaborador sem nome'}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-indigo-600">
                                      <StarSolidIcon className="h-3 w-3" />
                                      Escolhido
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const selectedCandidates =
                            slot.candidates?.filter((candidate) => candidate?.isChosen) ?? [];
                          if (!selectedCandidates.length) {
                            return null;
                          }

                          return (
                            <div className="flex flex-wrap gap-2">
                              {selectedCandidates.slice(0, 3).map((candidate) => {
                                const initials = (candidate?.collaboratorName ?? '??').slice(0, 2).toUpperCase();
                                return (
                                  <div
                                    key={candidate?.collaboratorId ?? candidate?.collaboratorName}
                                    className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-sm"
                                  >
                                    {candidate?.collaboratorAvatar ? (
                                      <img
                                        src={candidate.collaboratorAvatar}
                                        alt={candidate.collaboratorName ?? 'Colaborador selecionado'}
                                        className="h-6 w-6 rounded-full object-cover"
                                      />
                                    ) : (
                                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-600">
                                        {initials}
                                      </span>
                                    )}
                                    <span className="max-w-[120px] truncate">
                                      {candidate?.collaboratorName ?? 'Sem nome'}
                                    </span>
                                  </div>
                                );
                              })}
                              {selectedCandidates.length > 3 ? (
                                <span className="text-[11px] font-medium text-gray-500">
                                  +{selectedCandidates.length - 3} outros
                                </span>
                              ) : null}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">Disponibilidades de colaboradores</h1>
          <p className="text-sm text-gray-500">
            Acompanhe as necessidades registradas e identifique oportunidades de alocação com mais agilidade.
          </p>
        </div>

        <button
          type="button"
          className="btn-primary self-start sm:self-auto"
          onClick={() => navigate('/collaborators/availabilities/form')}
        >
          Nova necessidade
        </button>
      </div>

      {content}
    </div>
  );
}
